<template>
  <main dir="rtl" class="flex flex-col h-[calc(100vh-64px)] overflow-hidden bg-white">

    <!-- Messages area -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto px-4 py-6">
      <div class="mx-auto max-w-2xl space-y-6">

        <!-- Welcome / Suggestions -->
        <div v-if="messages.length === 0" class="flex flex-col items-center space-y-6 py-10">
          <h2 class="text-xl font-semibold text-gray-700 text-center">كيف يمكنني مساعدتك اليوم؟</h2>
          <div class="w-full space-y-4">
            <div v-for="cat in SUGGESTION_CATEGORIES" :key="cat.label" class="space-y-2">
              <p class="text-sm font-medium text-gray-400 px-1">{{ cat.emoji }} {{ cat.label }}</p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="s in cat.suggestions"
                  :key="s"
                  @click="input = s"
                  class="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors text-right"
                >
                  {{ s }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Messages -->
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="flex"
          :class="msg.role === 'user' ? 'justify-start' : 'justify-end'"
        >
          <!-- User bubble -->
          <div
            v-if="msg.role === 'user'"
            class="max-w-[75%] rounded-2xl bg-[#4d6bfe] px-4 py-2.5 text-sm text-white leading-relaxed"
          >
            {{ msg.content }}
          </div>

          <!-- Assistant bubble -->
          <div v-else class="max-w-[85%] text-sm text-gray-800 leading-relaxed" v-html="renderMarkdown(msg.content)" />
        </div>

        <!-- Loading indicator -->
        <div v-if="isLoading" class="flex items-center gap-2">
          <div class="h-7 w-7 rounded-full border border-gray-200 flex items-center justify-center">
            <svg class="animate-spin h-4 w-4 text-[#4d6bfe]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          </div>
          <span class="text-xs text-gray-400 animate-pulse">{{ statusMessage || 'جاري التفكير...' }}</span>
        </div>

      </div>
    </div>

    <!-- Input area -->
    <div class="px-4 py-3 bg-white">
      <div class="mx-auto max-w-2xl">
        <div class="flex items-end gap-2 rounded-2xl bg-gray-50 px-3 py-2">
          <textarea
            ref="textareaRef"
            v-model="input"
            @keydown.enter.exact.prevent="handleSubmit"
            @keydown.shift.enter="null"
            @input="autoResize"
            rows="1"
            placeholder="اكتب رسالتك..."
            class="flex-1 resize-none bg-transparent text-sm text-gray-800 placeholder:text-gray-400 outline-none border-0 max-h-32 leading-relaxed"
          />
          <button
            @click="handleSubmit"
            :disabled="!input.trim() || isLoading"
            class="flex-shrink-0 h-8 w-8 rounded-full bg-[#4d6bfe] flex items-center justify-center text-white disabled:opacity-40 hover:bg-[#2563eb] transition-colors"
          >
            <svg v-if="!isLoading" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="1" />
            </svg>
          </button>
        </div>
        <p class="mt-1.5 text-center text-xs text-gray-400">
          اضغط <kbd class="rounded border bg-gray-100 px-1 font-mono text-[10px]">Enter</kbd> للإرسال،
          <kbd class="rounded border bg-gray-100 px-1 font-mono text-[10px]">Shift</kbd>+<kbd class="rounded border bg-gray-100 px-1 font-mono text-[10px]">Enter</kbd> لسطر جديد
        </p>
      </div>
    </div>

  </main>
</template>

<script>
let msgIdCounter = 0
function genId() { return `msg-${++msgIdCounter}-${Date.now()}` }

const AGENT_URL = '/agent/stream'

const SUGGESTION_CATEGORIES = [
  {
    label: 'حضور وغياب',
    emoji: '📅',
    suggestions: [
      'هل حضر الطالب [الاسم] اليوم؟',
      'متى آخر مرة حضر [الاسم]؟',
      'كم مرة حضر [الاسم] هذا الشهر؟',
    ],
  },
  {
    label: 'تقدم الطالب',
    emoji: '📊',
    suggestions: [
      'ما تقدم الطالب [الاسم] في الحفظ؟',
      'ما ملاحظات المعلم على [الاسم]؟',
      'وش الجدول المسجل فيه [الاسم]؟',
    ],
  },
  {
    label: 'المدارسة',
    emoji: '🔵',
    suggestions: [
      'هل حضر [الاسم] المدارسة أمس؟',
      'كم جلسة مدارسة حضر [الاسم] هذا الشهر؟',
    ],
  },
  {
    label: 'إحصائيات عامة',
    emoji: '📋',
    suggestions: [
      'كم عدد الطلاب في النظام؟',
      'كم معلم مسجل؟',
      'من أكثر طالب حضوراً هذا الشهر؟',
    ],
  },
]

export default {
  name: 'Hafed',
  data() {
    return {
      messages: [],
      input: '',
      isLoading: false,
      statusMessage: null,
      sessionId: `session-${Date.now()}`,
      SUGGESTION_CATEGORIES,
    }
  },
  methods: {
    async handleSubmit() {
      if (!this.input.trim() || this.isLoading) return

      const userMsg = { id: genId(), role: 'user', content: this.input }
      const assistantId = genId()
      const assistantMsg = { id: assistantId, role: 'assistant', content: '' }

      this.messages.push(userMsg, assistantMsg)
      const question = this.input
      this.input = ''
      this.isLoading = true
      this.$nextTick(() => this.scrollToBottom())

      try {
        const url = new URL(AGENT_URL, window.location.origin)
        url.searchParams.set('message', question)
        url.searchParams.set('sessionId', this.sessionId)

        const token = localStorage.getItem('token')
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        })

        if (response.status === 401) {
          const idx = this.messages.findIndex(m => m.id === assistantId)
          if (idx !== -1) this.messages[idx] = { ...this.messages[idx], content: 'ليس لديك صلاحية للوصول إلى المساعد الذكي. تواصل مع المشرف.' }
          return
        }

        if (!response.ok) throw new Error('Stream connection failed')

        const reader = response.body?.getReader()
        const decoder = new TextDecoder()
        let fullContent = ''
        let buffer = ''

        if (!reader) throw new Error('No reader')

        let streaming = true
        while (streaming) {
          const { done, value } = await reader.read()
          if (done) { streaming = false; break }

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            const clean = line.replace(/\r$/, '')
            if (!clean.startsWith('data: ') && clean !== 'data:') continue

            const raw = clean.startsWith('data: ') ? clean.substring(6) : ''
            let content
            try {
              const json = JSON.parse(raw)
              if (json && typeof json === 'object' && json.type === 'status') {
                this.statusMessage = json.data ?? null
                continue
              }
              if (json && typeof json === 'object' && json.type === 'meta') continue
              if (json && typeof json === 'object' && 'data' in json) {
                this.statusMessage = null
                content = json.data ?? ''
              } else {
                content = raw
              }
            } catch {
              content = raw
            }

            if (content) {
              fullContent += content
              const idx = this.messages.findIndex(m => m.id === assistantId)
              if (idx !== -1) this.messages[idx] = { ...this.messages[idx], content: fullContent }
              this.$nextTick(() => this.scrollToBottom())
            }
          }
        }

        if (!fullContent) {
          const idx = this.messages.findIndex(m => m.id === assistantId)
          if (idx !== -1) this.messages[idx] = { ...this.messages[idx], content: 'عذراً، لم أستطع الرد.' }
        }
      } catch {
        const idx = this.messages.findIndex(m => m.id === assistantId)
        if (idx !== -1) this.messages[idx] = { ...this.messages[idx], content: 'عذراً، حدث خطأ أثناء الاتصال بالخادم.' }
      } finally {
        this.isLoading = false
        this.statusMessage = null
        this.$nextTick(() => this.scrollToBottom())
      }
    },

    scrollToBottom() {
      const el = this.$refs.messagesContainer
      if (el) el.scrollTop = el.scrollHeight
    },

    autoResize(e) {
      const el = e.target
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 128) + 'px'
    },

    renderMarkdown(text) {
      if (!text) return ''
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code class="bg-gray-100 rounded px-1 text-xs">$1</code>')
        .replace(/\n/g, '<br>')
    },
  },
}
</script>
